import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import ContinueExploring from '../components/ContinueExploring';
import { AffiliateStrip } from '../components/AffiliateStrip';
import { ProductPricingCTA, FlagshipStrip } from '../components/FlagshipFunnel';
import {
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  Building2,
  ClipboardList,
  Bell,
  Truck,
  CalendarCheck,
  BarChart3,
  CheckCircle2,
  Mail,
} from 'lucide-react';
import { useT } from '../i18n';

/* =================================================================
   /ai/catering-ai — Catering AI flagship (being finalized).

   Positioning mirrors Index AI and AI Agents flagship pages: a
   confident, premium product surface for a capability that is
   anticipated rather than live. The page sells the vision, lists
   the future capabilities, and captures interest via the existing
   #audit form on the homepage (used as the early-access waitlist).
   ================================================================= */

const PAGE_PATH = '/ai/catering-ai';

export default function CateringAiPage() {
  const { lang } = useT();
  const isEs = lang === 'es';

  const copy = isEs
    ? {
        eyebrow: 'EN FASE FINAL · DISPONIBLE MUY PRONTO',
        h1A: 'El pedido de catering que hoy no respondes,',
        h1B: 'mañana lo cierra tu competencia.',
        intro:
          'Las empresas de tu zona piden catering cada semana — y se lo dan al primero que responde con una propuesta seria. Catering AI opera ese negocio por ti: capta empresas, responde al instante y confirma pedidos. Sin llamadas perdidas, sin emails sin responder, sin oportunidades que se enfrían.',
        primaryCta: 'Pedir Acceso Anticipado',
        secondaryCta: 'Hablar con Rachel',
        phases: {
          eyebrow: 'LA VISIÓN',
          title: 'Lanza. Crece. Escala.',
          body: 'Catering AI está en su fase final de desarrollo. Una sola plataforma gestionada para llevar tu operación de catering desde el primer pedido hasta un motor de ingresos escalable.',
          items: [
            { n: '01', title: 'Lanza', body: 'Abre tu canal de catering con una tienda ecommerce, menús configurados y pagos seguros — todo montado y gestionado por KITXENS.' },
            { n: '02', title: 'Crece', body: 'La visibilidad IA y tu equipo de IA atraen compradores corporativos, responden al instante y convierten consultas en pedidos confirmados.' },
            { n: '03', title: 'Escala', body: 'Logística, insights y automatización de pedidos recurrentes convierten cada evento en ingresos repetibles — sin contratar más personal.' },
          ],
        },
        scope: {
          eyebrow: 'POR QUÉ AHORA',
          title: 'El catering es el canal más rentable. Y el peor gestionado.',
          body:
            'Restaurantes con catering corporativo activo facturan entre 2 y 4 veces más por cliente. Pero la mayoría lo gestiona con emails sueltos, hojas de cálculo y respuestas a las 11pm. Catering AI elimina esa fricción.',
          stats: [
            { value: '2–4×', label: 'mayor ticket promedio en pedidos corporativos vs. dine-in' },
            { value: '67%', label: 'de leads de catering se pierden por respuesta tardía' },
            { value: '24/7', label: 'oportunidades activas: oficinas, eventos, escuelas, hospitales' },
          ],
        },
        capabilities: {
          eyebrow: 'QUÉ HARÁ CATERING AI',
          title: 'Una operación de catering completa, orquestada por IA.',
          items: [
            { icon: 'Building2', title: 'Captación de empresas', body: 'Descubre oficinas, escuelas y eventos en tu zona que necesitan catering — antes de que pidan tres presupuestos.' },
            { icon: 'ClipboardList', title: 'Propuestas en minutos', body: 'Genera presupuestos profesionales adaptados al cliente desde un brief de WhatsApp o email.' },
            { icon: 'Bell', title: 'Respuesta 24/7', body: 'Rachel atiende cada consulta de catering al instante, en inglés o español, en tu voz de marca.' },
            { icon: 'CalendarCheck', title: 'Coordinación de pedidos', body: 'Eva conecta menú, cocina, repartidor y cliente en un único calendario operativo sin tareas manuales.' },
            { icon: 'Truck', title: 'Logística de entrega', body: 'Optimiza rutas, ventanas horarias y embalaje — y avisa al cliente automáticamente en cada hito.' },
            { icon: 'BarChart3', title: 'Insights de cuenta', body: 'Identifica clientes con potencial de recurrencia y le propone a Sonny cuándo activar campañas o promociones.' },
          ],
        },
        flow: {
          eyebrow: 'CÓMO FUNCIONARÁ',
          title: 'De un email frío a un pedido confirmado, en horas.',
          body: 'El flujo típico de un nuevo cliente corporativo entrando en Catering AI.',
          steps: [
            { title: 'Lead detectado', body: 'Stan descubre una nueva oficina que pidió catering en tu zona el mes pasado.' },
            { title: 'Outreach inteligente', body: 'Penny prepara un ángulo personalizado y Sonny lo activa por LinkedIn / email.' },
            { title: 'Consulta instantánea', body: 'Cuando responden, Rachel atiende y captura: número de comensales, fecha, dietas.' },
            { title: 'Propuesta en minutos', body: 'Catering AI genera presupuesto, menú y términos. Tu equipo solo revisa.' },
            { title: 'Confirmación + logística', body: 'Eva coordina cocina, packaging y entrega. Linda formaliza el acuerdo si aplica.' },
            { title: 'Seguimiento', body: 'Después del evento, Catering AI propone próximos pedidos basados en el patrón del cliente.' },
          ],
        },
        accessTitle: '¿Quieres ser de los primeros restaurantes con Catering AI?',
        accessBody:
          'Estamos abriendo acceso anticipado a operadores que ya tienen tráfico de catering y quieren multiplicarlo. Solicita una auditoría gratis y te diremos si encajas.',
        backToTeam: 'Ver el equipo operativo',
      }
    : {
        eyebrow: 'BEING FINALIZED · AVAILABLE SOON',
        h1A: "The catering order you don't answer today,",
        h1B: 'your competitor closes tomorrow.',
        intro:
          "Businesses in your area order catering every single week — and they give it to the first restaurant that replies with a serious proposal. Catering AI runs that business for you: it finds companies, replies instantly and confirms orders. No missed calls, no unanswered emails, no opportunities that go cold.",
        primaryCta: 'Request Early Access',
        secondaryCta: 'Talk to Rachel',
        phases: {
          eyebrow: 'THE VISION',
          title: 'Launch. Grow. Scale.',
          body: 'Catering AI is currently being finalized. One managed platform to take your catering operation from its first order to a scalable revenue engine.',
          items: [
            { n: '01', title: 'Launch', body: 'Open your catering channel with an ecommerce storefront, configured menus and secure payments — fully set up and managed by KITXENS.' },
            { n: '02', title: 'Grow', body: 'AI visibility and your AI workforce bring corporate buyers to you, respond instantly and turn inquiries into confirmed orders.' },
            { n: '03', title: 'Scale', body: 'Logistics, insights and repeat-order automation turn every event into recurring revenue — without adding headcount.' },
          ],
        },
        scope: {
          eyebrow: 'WHY NOW',
          title: 'Catering is the most profitable channel. And the worst managed.',
          body:
            'Restaurants with active corporate catering bill 2–4× more per customer. Yet most still run it on scattered emails, spreadsheets and 11pm replies. Catering AI removes that friction.',
          stats: [
            { value: '2–4×', label: 'higher average order vs. dine-in' },
            { value: '67%', label: 'of catering leads lost to slow replies' },
            { value: '24/7', label: 'live opportunities: offices, events, schools, hospitals' },
          ],
        },
        capabilities: {
          eyebrow: 'WHAT CATERING AI WILL DO',
          title: 'A complete catering operation, orchestrated by AI.',
          items: [
            { icon: 'Building2', title: 'Corporate discovery', body: 'Surfaces offices, schools and events near you that need catering — before they ask three competitors.' },
            { icon: 'ClipboardList', title: 'Proposals in minutes', body: 'Generates professional, brand-aligned quotes from a WhatsApp or email brief.' },
            { icon: 'Bell', title: '24/7 response', body: 'Rachel handles every catering inquiry instantly, in English or Spanish, in your brand voice.' },
            { icon: 'CalendarCheck', title: 'Order coordination', body: 'Eva connects menu, kitchen, driver and client into a single operational calendar — no manual chasing.' },
            { icon: 'Truck', title: 'Delivery logistics', body: 'Optimises routes, time windows and packaging — and notifies the client automatically at each milestone.' },
            { icon: 'BarChart3', title: 'Account insights', body: 'Spots customers with repeat potential and tells Sonny when to activate campaigns or promos.' },
          ],
        },
        flow: {
          eyebrow: 'HOW IT WORKS',
          title: 'From cold email to confirmed order, in hours.',
          body: 'The typical flow of a new corporate customer entering Catering AI.',
          steps: [
            { title: 'Lead detected', body: 'Stan discovers a new office that ordered catering in your area last month.' },
            { title: 'Smart outreach', body: 'Penny prepares a personal angle and Sonny activates it via LinkedIn / email.' },
            { title: 'Instant inquiry', body: 'When they reply, Rachel handles the conversation and captures guests, date, diets.' },
            { title: 'Proposal in minutes', body: 'Catering AI drafts the quote, menu and terms. Your team only reviews.' },
            { title: 'Confirm + logistics', body: 'Eva coordinates kitchen, packaging and delivery. Linda formalises the agreement if needed.' },
            { title: 'Follow-through', body: 'After the event, Catering AI proposes the next orders based on the customer’s pattern.' },
          ],
        },
        accessTitle: 'Want to be one of the first restaurants with Catering AI?',
        accessBody:
          'We are opening early access to operators who already have catering traffic and want to multiply it. Request a free audit and we’ll tell you if you fit.',
        backToTeam: 'Meet the operating team',
      };

  const ICONS = { Building2, ClipboardList, Bell, Truck, CalendarCheck, BarChart3 };

  const ldProduct = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Catering AI by Kitxens',
    serviceType: 'AI-powered corporate catering operations',
    description: copy.intro,
    provider: { '@type': 'Organization', name: 'Kitxens', url: 'https://www.kitxens.com' },
    brand: { '@type': 'Brand', name: 'Kitxens' },
    category: 'Restaurant Technology / Catering Operations',
    url: `https://kitxens.com${PAGE_PATH}`,
  };

  return (
    <div className="bg-white" data-testid="catering-ai-page">
      <SEO
        path={PAGE_PATH}
        title={isEs
          ? 'Catering AI — Catering corporativo en piloto automático'
          : 'Catering AI — Corporate catering on autopilot'}
        description={copy.intro}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(ldProduct)}</script>
      </Helmet>

      {/* HERO */}
      <section className="relative bg-[#0B0D12] text-white pt-28 pb-24 lg:pt-36 lg:pb-32 overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-0"
          style={{
            background:
              'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(247,127,17,0.20), transparent 60%)',
          }}
        />
        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-[#F77F11]/30 bg-[#F77F11]/10 px-3 py-1.5 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-[#F77F11]" />
              <p className="text-[10.5px] tracking-[0.22em] font-bold uppercase text-[#F77F11]" data-testid="catering-eyebrow">
                {copy.eyebrow}
              </p>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-[64px] font-semibold tracking-[-0.025em] leading-[1.04]">
              {copy.h1A}<br />
              <span className="bg-gradient-to-r from-[#F77F11] to-[#ff9b4a] bg-clip-text text-transparent pb-[0.12em] -mb-[0.12em]">
                {copy.h1B}
              </span>
            </h1>
            <p className="mt-7 text-neutral-300 text-base sm:text-lg leading-relaxed max-w-2xl">
              {copy.intro}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link
                to="/#audit"
                className="inline-flex items-center justify-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] text-white font-semibold text-[15px] rounded-full h-12 px-6 transition-colors"
                data-testid="catering-primary-cta"
              >
                {copy.primaryCta} <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://wa.me/14085901102"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1fb955] text-white font-semibold text-[15px] rounded-full h-12 px-6 transition-colors"
                data-testid="catering-secondary-cta"
              >
                {copy.secondaryCta} <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* LAUNCH / GROW / SCALE VISION */}
      <section className="bg-[#0B0D12] text-white pb-24 lg:pb-28 -mt-2">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="border-t border-white/10 pt-14">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase mb-3">{copy.phases.eyebrow}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-semibold tracking-[-0.02em] leading-[1.1]">{copy.phases.title}</h2>
            <p className="mt-4 text-neutral-300 text-base sm:text-[17px] leading-relaxed max-w-2xl">{copy.phases.body}</p>
            <div className="mt-10 grid sm:grid-cols-3 gap-4">
              {copy.phases.items.map((ph, i) => (
                <motion.div
                  key={ph.n}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-6"
                  data-testid={`catering-phase-${i}`}
                >
                  <p className="text-[11px] tracking-[0.25em] font-bold text-[#F77F11]">{ph.n}</p>
                  <h3 className="mt-3 text-xl font-semibold tracking-[-0.01em]">{ph.title}</h3>
                  <p className="mt-2.5 text-neutral-300 text-[14px] leading-relaxed">{ph.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SCOPE / WHY NOW */}
      <section className="bg-white py-24 lg:py-28">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase mb-3">
              {copy.scope.eyebrow}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-bold tracking-[-0.02em] leading-[1.1] text-neutral-900">
              {copy.scope.title}
            </h2>
            <p className="mt-4 text-neutral-600 text-base sm:text-[17px] leading-relaxed">
              {copy.scope.body}
            </p>
          </div>
          <div className="mt-12 grid sm:grid-cols-3 gap-4">
            {copy.scope.stats.map((s, i) => (
              <motion.div
                key={s.value}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
                className="rounded-2xl border border-neutral-200 bg-neutral-50/60 p-6"
                data-testid={`catering-stat-${i}`}
              >
                <p className="text-4xl lg:text-5xl font-bold tracking-[-0.025em] bg-gradient-to-r from-[#F77F11] to-[#ff9b4a] bg-clip-text text-transparent pb-[0.12em] -mb-[0.12em]">
                  {s.value}
                </p>
                <p className="mt-3 text-neutral-700 text-[14.5px] leading-relaxed">
                  {s.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="bg-neutral-50 py-24 lg:py-28 border-y border-neutral-200">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-12">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase mb-3">
              {copy.capabilities.eyebrow}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-bold tracking-[-0.02em] leading-[1.1] text-neutral-900">
              {copy.capabilities.title}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {copy.capabilities.items.map((it, i) => {
              const I = ICONS[it.icon] || Sparkles;
              return (
                <motion.div
                  key={it.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  whileHover={{ y: -3 }}
                  className="rounded-2xl border border-neutral-200 bg-white p-6 hover:border-[#F77F11] hover:shadow-[0_28px_70px_-30px_rgba(247,127,17,0.4)] transition-all duration-300"
                  data-testid={`catering-cap-${i}`}
                >
                  <span className="inline-flex w-11 h-11 rounded-xl bg-orange-50 items-center justify-center">
                    <I className="w-5 h-5 text-[#F77F11]" />
                  </span>
                  <h3 className="mt-5 text-neutral-900 font-bold text-[17.5px] tracking-[-0.005em] leading-snug">
                    {it.title}
                  </h3>
                  <p className="mt-2 text-neutral-600 text-[14px] leading-relaxed">
                    {it.body}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FLOW */}
      <section className="bg-white py-24 lg:py-28">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-12">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase mb-3">
              {copy.flow.eyebrow}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-bold tracking-[-0.02em] leading-[1.1] text-neutral-900">
              {copy.flow.title}
            </h2>
            <p className="mt-4 text-neutral-600 text-base sm:text-[17px] leading-relaxed">
              {copy.flow.body}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {copy.flow.steps.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="rounded-2xl border border-neutral-200 bg-white p-6"
                data-testid={`catering-flow-${i}`}
              >
                <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-neutral-400 mb-3">
                  {String(i + 1).padStart(2, '0')} · {s.title}
                </p>
                <p className="text-neutral-700 text-[14.5px] leading-relaxed">
                  {s.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING FUNNEL */}
      <ProductPricingCTA product="catering" />

      {/* EARLY ACCESS CLOSING */}
      <section className="bg-[#0B0D12] text-white py-24 lg:py-32">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 mb-5">
              <Mail className="w-3.5 h-3.5 text-[#F77F11]" />
              <p className="text-[10.5px] tracking-[0.22em] font-bold uppercase text-[#F77F11]">
                {isEs ? 'ACCESO ANTICIPADO' : 'EARLY ACCESS'}
              </p>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-semibold tracking-[-0.02em] leading-[1.1]">
              {copy.accessTitle}
            </h2>
            <p className="mt-5 text-neutral-300 text-base sm:text-[17px] leading-relaxed max-w-2xl">
              {copy.accessBody}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link
                to="/#audit"
                className="inline-flex items-center justify-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] text-white font-semibold text-[15px] rounded-full h-12 px-6 transition-colors"
                data-testid="catering-bottom-cta"
              >
                {copy.primaryCta} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/operating-intelligence"
                className="inline-flex items-center justify-center gap-2 bg-white/[0.06] hover:bg-white/[0.12] border border-white/15 text-white font-semibold text-[15px] rounded-full h-12 px-6 transition-colors"
                data-testid="catering-bottom-link-team"
              >
                {copy.backToTeam} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-2 text-[12.5px] text-neutral-400">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              <span>{isEs
                ? 'Sin compromiso. Respondemos en 48 horas.'
                : 'No commitment. We reply within 48 hours.'}</span>
            </div>
          </div>
        </div>
      </section>

      <FlagshipStrip current="catering" />
      <AffiliateStrip className="py-14" />
      <ContinueExploring />
    </div>
  );
}
